import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Title,
  Autocomplete,
  TextInput,
  Textarea,
  Select,
  Button,
  Stack,
  Group,
  Text,
  Divider,
  Badge,
  Modal,
  ThemeIcon,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { itemsApi } from '../api/items';
import { autocompleteApi } from '../api/autocomplete';
import { categoriesApi } from '../api/categories';
import { usePendingImages } from '../hooks/usePendingImages';
import { buildCategorySelectData } from '../utils/categories';
import { buildColorSynonymMap, buildCanonicalBrands, buildCategorySynonymMap, detectFromText } from '../utils/detectFromText';
import { ImageUpload } from '../components/ImageUpload';
import { type CreateItemInput } from '../types';
import { pageContainer } from '../styles/theme.css';
import { getSemester } from '../utils/semester';

function todayIso() {
  return new Date().toISOString().split('T')[0];
}

function defaultDiscardDate() {
  const d = new Date();
  d.setMonth(d.getMonth() + 6);
  return d.toISOString().split('T')[0];
}

export function CreateItem() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { pending, pendingImageUrls, previewImages, addImage, deleteImage } = usePendingImages();
  const [matchModal, setMatchModal] = useState<{ itemId: number; count: number } | null>(null);
  const [autoFilled, setAutoFilled] = useState<{ color: boolean; brand: boolean; category: boolean }>({ color: false, brand: false, category: false });

  const { data: acData } = useQuery({
    queryKey: ['autocomplete'],
    queryFn: autocompleteApi.get,
    staleTime: 60_000,
  });

  const { data: catData } = useQuery({
    queryKey: ['categories'],
    queryFn: categoriesApi.get,
    staleTime: Infinity,
  });

  const categorySelectData = buildCategorySelectData(catData);
  const colorBySynonym = buildColorSynonymMap(catData);
  const canonicalBrands = buildCanonicalBrands(catData);
  const categoryBySynonym = buildCategorySynonymMap(catData);

  const form = useForm<CreateItemInput>({
    initialValues: {
      title: '',
      category_id: 'andet',
      color: null,
      brand: null,
      distinctive_features: null,
      found_date: todayIso(),
      discard_after: defaultDiscardDate(),
      storage_location: 'Fjernlager',
      notes: null,
    },
    validate: {
      title: (v) => (v.trim() ? null : 'Titel er påkrævet'),
      category_id: (v) => (v ? null : 'Kategori er påkrævet'),
      found_date: (v) => (v ? null : 'Fundet dato er påkrævet'),
    },
  });

  const createMutation = useMutation({
    mutationFn: async (values: CreateItemInput) => {
      const item = await itemsApi.create(values);
      await Promise.all(pending.map((p) => itemsApi.addImage(item.id, p.url, p.ziplineId)));
      return item;
    },
    onSuccess: (item) => {
      qc.invalidateQueries({ queryKey: ['items'] });
      qc.invalidateQueries({ queryKey: ['suggestions'] });
      if (item.matches_found > 0) {
        setMatchModal({ itemId: item.id, count: item.matches_found });
      } else {
        notifications.show({ message: 'Genstand registreret', color: 'green' });
        navigate(`/items/${item.id}`);
      }
    },
    onError: (err) => {
      notifications.show({ message: (err as Error).message, color: 'red' });
    },
  });


  const colors = acData?.colors ?? [];
  const brands = acData?.brands ?? [];
  const titles = acData?.titles ?? [];

  function handleTitleChange(v: string) {
    form.setFieldValue('title', v);
    const { detectedColor, detectedBrand, detectedCategory } = detectFromText(v, colorBySynonym, canonicalBrands, categoryBySynonym);
    const canFillColor = !form.values.color || autoFilled.color;
    const canFillBrand = !form.values.brand || autoFilled.brand;
    const canFillCategory = form.values.category_id === 'andet' || autoFilled.category;
    if (canFillColor) form.setFieldValue('color', detectedColor);
    if (canFillBrand) form.setFieldValue('brand', detectedBrand);
    if (canFillCategory) form.setFieldValue('category_id', detectedCategory ?? 'andet');
    setAutoFilled({
      color: canFillColor && !!detectedColor,
      brand: canFillBrand && !!detectedBrand,
      category: canFillCategory && !!detectedCategory,
    });
  }

  return (
    <div className={pageContainer}>
      <Title order={2} mb="md">
        Registrer ny genstand
      </Title>

      <form onSubmit={form.onSubmit((v) => createMutation.mutate(v))}>
        <Stack gap="sm">
          <Autocomplete
            label="Titel"
            placeholder="f.eks. Sort læderjakke"
            required
            data={form.values.title.length >= 2 ? titles : []}
            value={form.values.title}
            onChange={handleTitleChange}
            error={form.errors.title}
          />

          <Select
            label="Kategori"
            placeholder="Vælg kategori"
            required
            data={categorySelectData}
            searchable
            description={autoFilled.category ? 'Autoudfyldt fra titel' : undefined}
            descriptionProps={autoFilled.category ? { style: { color: 'var(--mantine-color-red-4)' } } : undefined}
            {...form.getInputProps('category_id')}
            onChange={(v) => { form.setFieldValue('category_id', v ?? 'andet'); setAutoFilled((p) => ({ ...p, category: false })); }}
          />

          <Group grow>
            <Autocomplete
              label="Farve"
              placeholder="f.eks. Sort"
              data={(form.values.color ?? '').length >= 2 ? colors : []}
              value={form.values.color ?? ''}
              description={autoFilled.color ? 'Autoudfyldt fra titel' : undefined}
              descriptionProps={autoFilled.color ? { style: { color: 'var(--mantine-color-red-4)' } } : undefined}
              onChange={(v) => { form.setFieldValue('color', v || null); setAutoFilled((p) => ({ ...p, color: false })); }}
            />
            <Autocomplete
              label="Mærke"
              placeholder="f.eks. Nike"
              data={(form.values.brand ?? '').length >= 2 ? brands : []}
              value={form.values.brand ?? ''}
              description={autoFilled.brand ? 'Autoudfyldt fra titel' : undefined}
              descriptionProps={autoFilled.brand ? { style: { color: 'var(--mantine-color-red-4)' } } : undefined}
              onChange={(v) => { form.setFieldValue('brand', v || null); setAutoFilled((p) => ({ ...p, brand: false })); }}
            />
          </Group>

          <Textarea
            label="Særlige kendetegn"
            placeholder="Særlige kendetegn der hjælper med identifikation"
            {...form.getInputProps('distinctive_features')}
          />

          <Group grow>
            <Stack gap={4}>
              <TextInput
                label="Fundet dato"
                type="date"
                required
                {...form.getInputProps('found_date')}
              />
              {form.values.found_date && (
                <Badge size="sm" variant="light" color="blue" w="fit-content">
                  {getSemester(form.values.found_date)}
                </Badge>
              )}
            </Stack>
            <TextInput label="Kassér efter" type="date" {...form.getInputProps('discard_after')} />
          </Group>

          <Select
            label="Opbevaringssted"
            data={[
              { label: 'Fjernlager', value: 'Fjernlager' },
              { label: 'Glemte sager kasse', value: 'Glemte sager kasse' },
            ]}
            {...form.getInputProps('storage_location')}
          />

          <Textarea
            label="Noter"
            placeholder="Intern note til personalet"
            {...form.getInputProps('notes')}
          />

          <Divider label="Billeder" />

          <ImageUpload
            existingImages={previewImages}
            onUpload={addImage}
            onDelete={deleteImage}
          />

          {pendingImageUrls.length > 0 && (
            <Text size="xs" c="dimmed">
              {pendingImageUrls.length} billede(r) klar til upload
            </Text>
          )}

          <Button type="submit" fullWidth size="lg" mt="sm" loading={createMutation.isPending}>
            Gem genstand
          </Button>
        </Stack>
      </form>

      <Modal
        opened={matchModal !== null}
        onClose={() => {
          if (matchModal) navigate(`/items/${matchModal.itemId}`);
          setMatchModal(null);
        }}
        withCloseButton={false}
        centered
        size="sm"
      >
        {matchModal && (
          <Stack align="center" gap="md" py="md">
            <ThemeIcon size={56} radius="xl" color="orange" variant="light">
              <Text size="xl">🔍</Text>
            </ThemeIcon>
            <Stack align="center" gap={4}>
              <Text fw={700} size="lg">Mulige match fundet!</Text>
              <Text c="dimmed" size="sm" ta="center">
                Genstanden matcher{' '}
                <b>{matchModal.count} efterlysning{matchModal.count !== 1 ? 'er' : ''}</b>.
                Vil du se forslagene nu?
              </Text>
            </Stack>
            <Group>
              <Button
                component={Link}
                to="/matches"
                color="orange"
                onClick={() => setMatchModal(null)}
              >
                Se matches
              </Button>
              <Button
                variant="default"
                onClick={() => {
                  navigate(`/items/${matchModal.itemId}`);
                  setMatchModal(null);
                }}
              >
                Fortsæt
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </div>
  );
}
